import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  Users, 
  Clock, 
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Filter,
  Download,
  Play,
  Pause,
  Settings,
  GitCompare,
  Eye,
  Award,
  Lightbulb,
  Activity,
  PieChart,
  LineChart,
  BarChart2
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  RadialLinearScale
} from 'chart.js';
import { Line, Bar, Pie, Doughnut, Radar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import PromptDiffViewer from './PromptDiffViewer';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  RadialLinearScale
);

const AdvancedExperimentAnalytics = ({ 
  analytics, 
  experiment, 
  onTimeRangeChange,
  onDrillDown
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState(30);
  const [selectedMetric, setSelectedMetric] = useState('conversion_rate');
  const [drillDownVersion, setDrillDownVersion] = useState(null);
  const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(true);
  const [inputAnalysisData, setInputAnalysisData] = useState(null);
  const [showStatisticalDetails, setShowStatisticalDetails] = useState(false);
  const [selectedAnalysisVersions, setSelectedAnalysisVersions] = useState([]);
  const [visualizationType, setVisualizationType] = useState('comparison'); // 'comparison', 'trend', 'distribution', 'radar'
  const [selectedChartMetric, setSelectedChartMetric] = useState('score'); // 'score', 'latency', 'success_rate', 'requests'

  // Color palette for consistent styling
  const chartColors = {
    primary: '#10b981', // celadon-600
    secondary: '#6b7280', // gray-500
    accent: '#3b82f6', // blue-500
    warning: '#f59e0b', // amber-500
    danger: '#ef4444', // red-500
    success: '#10b981' // emerald-500
  };

  const generateColorPalette = (count) => {
    const colors = [
      'rgba(16, 185, 129, 0.8)', // celadon
      'rgba(59, 130, 246, 0.8)', // blue
      'rgba(245, 158, 11, 0.8)', // amber
      'rgba(239, 68, 68, 0.8)', // red
      'rgba(139, 92, 246, 0.8)', // violet
      'rgba(34, 197, 94, 0.8)', // green
      'rgba(236, 72, 153, 0.8)', // pink
      'rgba(99, 102, 241, 0.8)' // indigo
    ];
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
  };

  // Helper functions - moved above useMemo to avoid hoisting issues
  const erf = (x) => {
    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  // Normal CDF approximation
  const normalCDF = (x) => {
    return 0.5 * (1 + erf(x / Math.sqrt(2)));
  };

  // Calculate confidence intervals
  const calculateConfidenceInterval = (rate, n, confidence = 0.95) => {
    if (n === 0) return { lower: 0, upper: 0 };
    
    const z = 1.96; // 95% confidence
    const margin = z * Math.sqrt((rate * (1 - rate)) / n);
    
    return {
      lower: Math.max(0, rate - margin),
      upper: Math.min(1, rate + margin)
    };
  };

  // Calculate latency confidence interval
  const calculateLatencyConfidenceInterval = (mean, n, stdDev = null) => {
    if (n === 0) return { lower: mean, upper: mean };
    
    // Estimate standard deviation if not provided (rough approximation)
    const estimatedStdDev = stdDev || mean * 0.3;
    const margin = 1.96 * (estimatedStdDev / Math.sqrt(n));
    
    return {
      lower: Math.max(0, mean - margin),
      upper: mean + margin
    };
  };

  // Calculate statistical significance between two versions
  const calculateStatisticalSignificance = (variant, control) => {
    const variantRequests = parseInt(variant.request_count) || 0;
    const controlRequests = parseInt(control.request_count) || 0;
    
    if (variantRequests < 30 || controlRequests < 30) {
      return { significant: false, reason: 'insufficient_sample_size' };
    }

    const variantScore = parseFloat(variant.avg_auto_eval_score) || 0;
    const controlScore = parseFloat(control.avg_auto_eval_score) || 0;
    
    // Handle edge case where control score is 0
    if (controlScore === 0) {
      return { 
        significant: false, 
        reason: 'control_score_zero',
        pValue: 1,
        tStatistic: 0,
        improvement: variantScore > 0 ? 100 : 0,
        recommendedAction: 'continue'
      };
    }
    
    // Simplified t-test calculation
    const pooledStdDev = 0.15; // Estimated based on typical eval scores
    const stdError = pooledStdDev * Math.sqrt(1/variantRequests + 1/controlRequests);
    const tStatistic = Math.abs(variantScore - controlScore) / stdError;
    const pValue = 2 * (1 - normalCDF(Math.abs(tStatistic)));
    const improvement = ((variantScore - controlScore) / controlScore) * 100;
    
    // Ensure all values are finite
    const safePValue = isFinite(pValue) ? pValue : 1;
    const safeTStatistic = isFinite(tStatistic) ? tStatistic : 0;
    const safeImprovement = isFinite(improvement) ? improvement : 0;
    
    return {
      significant: safePValue < 0.05,
      pValue: safePValue,
      tStatistic: safeTStatistic,
      improvement: safeImprovement,
      recommendedAction: variantScore > controlScore && safePValue < 0.05 ? 'promote' : 
                        variantScore < controlScore && safePValue < 0.05 ? 'stop' : 'continue'
    };
  };

  // Helper function to safely format numbers
  const safeToFixed = (value, decimals = 2) => {
    if (value === null || value === undefined || !isFinite(value)) {
      return '0.' + '0'.repeat(decimals);
    }
    return value.toFixed(decimals);
  };

  // Calculate real analytics data from the actual experiment data
  const calculateRealAnalytics = useMemo(() => {
    if (!analytics?.overview) return {
      totalUsers: 0,
      totalTests: 0,
      topPerformance: 0,
      avgLatency: 0
    };

    const totalTests = analytics.overview.reduce((sum, version) => 
      sum + (parseInt(version.request_count) || 0), 0);
    
    const totalUsers = analytics.overview.reduce((sum, version) => 
      sum + (parseInt(version.total_logs) || 0), 0);
    
    const topPerformance = Math.max(...analytics.overview.map(version => 
      parseFloat(version.avg_auto_eval_score) || 0));
    
    const avgLatency = analytics.overview.reduce((sum, version) => 
      sum + (parseFloat(version.avg_latency) || 0), 0) / analytics.overview.length;

    return {
      totalUsers,
      totalTests,
      topPerformance,
      avgLatency
    };
  }, [analytics]);

  // Enhanced metrics calculation with statistical significance
  const enhancedAnalytics = useMemo(() => {
    if (!analytics?.overview) return null;

    const versions = analytics.overview.map(version => {
      const requests = parseInt(version.request_count) || 0;
      const failures = parseInt(version.failure_count) || 0;
      const avgLatency = parseFloat(version.avg_latency) || 0;
      const avgScore = parseFloat(version.avg_auto_eval_score) || 0;
      
      // Calculate additional metrics
      const successRate = requests > 0 ? (requests - failures) / requests : 0;
      const failureRate = requests > 0 ? failures / requests : 0;
      
      // Calculate confidence intervals (using normal approximation)
      const successCI = calculateConfidenceInterval(successRate, requests);
      const scoreCI = calculateConfidenceInterval(avgScore, requests);
      const latencyCI = calculateLatencyConfidenceInterval(avgLatency, requests);
      
      // Statistical significance vs control
      const isControl = version.is_control;
      const controlVersion = analytics.overview.find(v => v.is_control);
      let significance = null;
      
      if (!isControl && controlVersion) {
        significance = calculateStatisticalSignificance(version, controlVersion);
      }

      return {
        ...version,
        successRate,
        failureRate,
        confidenceIntervals: {
          success: successCI,
          score: scoreCI,
          latency: latencyCI
        },
        statisticalSignificance: significance,
        sampleSize: requests
      };
    });

    return { ...analytics, overview: versions };
  }, [analytics]);

  // Chart data generators
  const generateComparisonChartData = () => {
    if (!enhancedAnalytics?.overview) return null;

    const versions = enhancedAnalytics.overview;
    const labels = versions.map(v => v.version_name || `Version ${v.version_id}`);
    const colors = generateColorPalette(versions.length);

    const datasets = {
      score: {
        label: 'Average Score',
        data: versions.map(v => parseFloat(v.avg_auto_eval_score) || 0),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      },
      latency: {
        label: 'Average Latency (ms)',
        data: versions.map(v => parseFloat(v.avg_latency) || 0),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      },
      success_rate: {
        label: 'Success Rate (%)',
        data: versions.map(v => (v.successRate * 100)),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      },
      requests: {
        label: 'Total Requests',
        data: versions.map(v => parseInt(v.request_count) || 0),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      }
    };

    return {
      labels,
      datasets: [datasets[selectedChartMetric]]
    };
  };

  const generateTrendChartData = () => {
    if (!enhancedAnalytics?.timeline) {
      // Mock trend data for demonstration
      const days = Array.from({ length: selectedTimeRange }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (selectedTimeRange - i - 1));
        return date.toISOString().split('T')[0];
      });

      const versions = enhancedAnalytics?.overview || [];
      
      return {
        labels: days,
        datasets: versions.map((version, index) => ({
          label: version.version_name || `Version ${version.version_id}`,
          data: days.map(() => {
            const baseValue = parseFloat(version.avg_auto_eval_score) || 0;
            return baseValue + (Math.random() - 0.5) * 0.1; // Add some variation
          }),
          borderColor: generateColorPalette(versions.length)[index],
          backgroundColor: generateColorPalette(versions.length)[index].replace('0.8', '0.1'),
          tension: 0.4,
          fill: false
        }))
      };
    }

    return enhancedAnalytics.timeline;
  };

  const generateDistributionChartData = () => {
    if (!enhancedAnalytics?.overview) return null;

    const versions = enhancedAnalytics.overview;
    const colors = generateColorPalette(versions.length);

    return {
      labels: versions.map(v => v.version_name || `Version ${v.version_id}`),
      datasets: [{
        data: versions.map(v => parseInt(v.request_count) || 0),
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 2
      }]
    };
  };

  const generateRadarChartData = () => {
    if (!enhancedAnalytics?.overview) return null;

    const versions = enhancedAnalytics.overview.slice(0, 3); // Limit to 3 versions for clarity
    const colors = generateColorPalette(versions.length);

    return {
      labels: ['Score', 'Success Rate', 'Speed', 'Reliability', 'Consistency'],
      datasets: versions.map((version, index) => {
        const score = (parseFloat(version.avg_auto_eval_score) || 0) * 20; // Scale to 0-100
        const successRate = version.successRate * 100;
        const speed = Math.max(0, 100 - (parseFloat(version.avg_latency) || 0) / 10); // Inverse latency
        const reliability = Math.max(0, 100 - (version.failureRate * 100));
        const consistency = Math.max(50, 100 - Math.random() * 30); // Mock consistency metric

        return {
          label: version.version_name || `Version ${version.version_id}`,
          data: [score, successRate, speed, reliability, consistency],
          borderColor: colors[index],
          backgroundColor: colors[index].replace('0.8', '0.2'),
          pointBackgroundColor: colors[index],
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: colors[index]
        };
      })
    };
  };

  const handleTimeRangeChange = (days) => {
    setSelectedTimeRange(days);
    onTimeRangeChange(days);
  };

  const handleDrillDownToggle = (version) => {
    const newSelection = drillDownVersion?.id === version.id ? null : version;
    setDrillDownVersion(newSelection);
    if (onDrillDown) {
      onDrillDown(newSelection);
    }
  };

  const handleAnalysisVersionSelect = (versionId) => {
    setSelectedAnalysisVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        return [prev[1], versionId]; // Replace first with new selection
      }
    });
  };

  const getPerformanceColor = (significance) => {
    if (significance?.significant) {
      return significance.improvement > 0 ? 'text-celadon-600' : 'text-gray-600';
    }
    return 'text-gray-500';
  };

  const getPerformanceIcon = (significance) => {
    if (!significance) return null;
    
    if (significance.significant) {
      return significance.improvement > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;
    }
    return <Clock className="h-4 w-4" />;
  };

  const getTopPerformingVersions = () => {
    if (!enhancedAnalytics?.overview) return [];
    return [...enhancedAnalytics.overview]
      .sort((a, b) => (parseFloat(b.avg_auto_eval_score) || 0) - (parseFloat(a.avg_auto_eval_score) || 0))
      .slice(0, 2);
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: 'Inter, system-ui, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(16, 185, 129, 0.5)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true
      }
    },
    scales: visualizationType === 'comparison' ? {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    } : {},
    elements: {
      bar: {
        borderRadius: 4
      },
      point: {
        radius: 4,
        hoverRadius: 6
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20
        }
      }
    }
  };

  // Chart rendering component
  const renderChart = () => {
    const chartData = {
      comparison: generateComparisonChartData(),
      trend: generateTrendChartData(),
      distribution: generateDistributionChartData(),
      radar: generateRadarChartData()
    }[visualizationType];

    if (!chartData) return null;

    const ChartComponent = {
      comparison: Bar,
      trend: Line,
      distribution: Doughnut,
      radar: Radar
    }[visualizationType];

    const options = visualizationType === 'radar' ? radarOptions : chartOptions;

    return (
      <div className="h-96 w-full">
        <ChartComponent data={chartData} options={options} />
      </div>
    );
  };

  if (!enhancedAnalytics?.overview) {
    return (
      <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
        <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-6" />
        <h3 className="text-xl font-medium text-gray-900 mb-3">No Analytics Data Available</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Start the experiment and generate some traffic to begin collecting performance data and insights.
        </p>
      </div>
    );
  }

  const topVersions = getTopPerformingVersions();
  const realAnalytics = calculateRealAnalytics;

  return (
    <div className="space-y-8">
      {/* Enhanced Header with Better Controls */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <BarChart3 className="h-6 w-6 text-celadon-600" />
            <h4 className="text-xl font-semibold text-gray-900">Advanced Analytics</h4>
          </div>
          <div className="flex items-center space-x-4">
            {/* Time Range Selector */}
            <select
              value={selectedTimeRange}
              onChange={(e) => handleTimeRangeChange(parseInt(e.target.value))}
              className={`text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-celadon-300 ${
                selectedTimeRange !== 30 ? 'bg-celadon-600 text-white shadow-sm' : ''
              }`}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Quick Stats Overview - Fixed with real data */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
          <Users className="h-8 w-8 text-celadon-500 mx-auto" />
          <div className="text-2xl font-bold text-gray-900 mt-2">{realAnalytics.totalUsers.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Total Executions</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
          <Target className="h-8 w-8 text-celadon-500 mx-auto" />
          <div className="text-2xl font-bold text-gray-900 mt-2">{realAnalytics.totalTests.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Total Requests</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
          <Award className="h-8 w-8 text-gray-500 mx-auto" />
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {safeToFixed(realAnalytics.topPerformance, 2)}
          </div>
          <div className="text-sm text-gray-500">Best Score</div>
        </div>
        <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
          <Activity className="h-8 w-8 text-gray-500 mx-auto" />
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {Math.round(realAnalytics.avgLatency)}ms
          </div>
          <div className="text-sm text-gray-500">Avg Latency</div>
        </div>
      </div>

      {/* Interactive Visualization Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <BarChart3 className="h-5 w-5 text-celadon-600" />
            <h5 className="text-lg font-semibold text-gray-900">Performance Visualizations</h5>
          </div>
          <div className="flex items-center space-x-4">
            {/* Visualization Type Selector */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">View:</label>
              <select
                value={visualizationType}
                onChange={(e) => setVisualizationType(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-celadon-300"
              >
                <option value="comparison">Comparison</option>
                <option value="trend">Trend Analysis</option>
                <option value="distribution">Distribution</option>
                <option value="radar">Performance Radar</option>
              </select>
            </div>

            {/* Metric Selector (for comparison charts) */}
            {visualizationType === 'comparison' && (
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Metric:</label>
                <select
                  value={selectedChartMetric}
                  onChange={(e) => setSelectedChartMetric(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-celadon-300"
                >
                  <option value="score">Average Score</option>
                  <option value="latency">Latency</option>
                  <option value="success_rate">Success Rate</option>
                  <option value="requests">Request Volume</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Chart Container */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
          {renderChart()}
        </div>

        {/* Chart Description */}
        <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
          <div className="text-sm text-gray-700">
            {visualizationType === 'comparison' && (
              <div className="flex items-start space-x-2">
                <BarChart2 className="h-4 w-4 mt-0.5 text-celadon-600" />
                <div>
                  <strong>Comparison Analysis:</strong> Compare {selectedChartMetric.replace('_', ' ')} across all experiment versions. 
                  Higher bars indicate better performance for most metrics (except latency, where lower is better).
                </div>
              </div>
            )}
            {visualizationType === 'trend' && (
              <div className="flex items-start space-x-2">
                <LineChart className="h-4 w-4 mt-0.5 text-celadon-600" />
                <div>
                  <strong>Trend Analysis:</strong> Track performance over time to identify patterns, improvements, or degradations. 
                  Look for consistent trends and sudden changes that might indicate issues or optimizations.
                </div>
              </div>
            )}
            {visualizationType === 'distribution' && (
              <div className="flex items-start space-x-2">
                <PieChart className="h-4 w-4 mt-0.5 text-celadon-600" />
                <div>
                  <strong>Traffic Distribution:</strong> Shows how requests are distributed across different versions. 
                  Useful for understanding traffic allocation and identifying which versions are receiving the most testing.
                </div>
              </div>
            )}
            {visualizationType === 'radar' && (
              <div className="flex items-start space-x-2">
                <Target className="h-4 w-4 mt-0.5 text-celadon-600" />
                <div>
                  <strong>Performance Radar:</strong> Multi-dimensional view comparing versions across key metrics. 
                  Larger areas indicate better overall performance. Compare shapes to identify strengths and weaknesses.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance Comparison Cards */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <GitCompare className="h-5 w-5 text-celadon-600" />
            <h5 className="text-lg font-semibold text-gray-900">Version Comparison</h5>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="confidence-intervals"
                checked={showConfidenceIntervals}
                onChange={(e) => setShowConfidenceIntervals(e.target.checked)}
                className="w-4 h-4 text-celadon-600 border-gray-300 rounded focus:ring-celadon-500"
              />
              <label htmlFor="confidence-intervals" className="text-sm text-gray-700">
                Show Confidence Intervals
              </label>
            </div>
            <button
              onClick={() => setSelectedAnalysisVersions(
                selectedAnalysisVersions.length > 0 ? [] : [enhancedAnalytics.overview[0]?.version_id]
              )}
              className="text-sm text-celadon-600 hover:text-celadon-800 flex items-center space-x-1"
            >
              <BarChart3 className="h-4 w-4" />
              <span>{selectedAnalysisVersions.length > 0 ? 'Hide' : 'Show'} Detailed Analysis</span>
            </button>
          </div>
        </div>

        {/* Version Cards */}
        {enhancedAnalytics.overview.map((version, index) => {
          const isExpanded = selectedAnalysisVersions.includes(version.version_id);
          const isSelected = selectedAnalysisVersions.length > 0 && selectedAnalysisVersions[0] === version.version_id;
          
          return (
            <div key={version.version_id} className={`mb-6 last:mb-0 bg-gray-50 rounded-xl border transition-all duration-200 ${
              isSelected ? 'border-celadon-500 bg-celadon-50 shadow-md' : 'border-gray-200'
            }`}>
              {/* Version Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-1 text-xs font-medium bg-celadon-100 text-celadon-600 rounded-full">
                        v{version.version_number || version.version_id}
                      </span>
                      {version.is_control && (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Control</span>
                      )}
                      {version.is_active && (
                        <span className="px-2 py-1 text-xs font-medium bg-celadon-100 text-celadon-600 rounded-full">Active</span>
                      )}
                    </div>
                    <h6 className="font-medium text-gray-900">
                      {version.version_name || `Version ${version.version_id}`}
                    </h6>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-500">
                      {version.total_logs} executions
                    </div>
                    <button
                      onClick={() => {
                        setSelectedAnalysisVersions(prev => 
                          prev.includes(version.version_id) 
                            ? prev.filter(id => id !== version.version_id)
                            : [...prev, version.version_id]
                        );
                      }}
                      className="text-gray-600 hover:text-gray-900 text-sm p-1"
                    >
                      {isExpanded ? '−' : '+'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Version Metrics */}
              <div className="p-6">
                <div className="grid grid-cols-4 gap-6 mb-6">
                  {/* Success Rate */}
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {safeToFixed(version.successRate * 100, 1)}%
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Success Rate</div>
                    {showConfidenceIntervals && version.confidenceIntervals?.success && (
                      <div className="text-xs text-gray-400">
                        CI: {safeToFixed(version.confidenceIntervals.success.lower * 100, 1)}% - {safeToFixed(version.confidenceIntervals.success.upper * 100, 1)}%
                      </div>
                    )}
                  </div>

                  {/* Average Score */}
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {safeToFixed(parseFloat(version.avg_auto_eval_score) || 0, 2)}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Avg Score</div>
                    {showConfidenceIntervals && version.confidenceIntervals?.score && (
                      <div className="text-xs text-gray-400">
                        CI: {safeToFixed(version.confidenceIntervals.score.lower, 2)} - {safeToFixed(version.confidenceIntervals.score.upper, 2)}
                      </div>
                    )}
                  </div>

                  {/* Latency */}
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {Math.round(parseFloat(version.avg_latency) || 0)}ms
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Avg Latency</div>
                    {showConfidenceIntervals && version.confidenceIntervals?.latency && (
                      <div className="text-xs text-gray-400">
                        CI: {Math.round(version.confidenceIntervals.latency.lower || 0)} - {Math.round(version.confidenceIntervals.latency.upper || 0)}ms
                      </div>
                    )}
                  </div>

                  {/* Failure Rate */}
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {safeToFixed(version.failureRate * 100, 1)}%
                    </div>
                    <div className="text-sm text-gray-500 mb-2">Failure Rate</div>
                    <div className="text-xs text-gray-400">
                      {version.failure_count} total failures
                      {version.auto_eval_failures && (
                        <div>• {version.auto_eval_failures} auto-eval</div>
                      )}
                      {version.system_failures && (
                        <div>• {version.system_failures} system</div>
                      )}
                      {version.quality_failures && (
                        <div>• {version.quality_failures} quality</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Statistical Significance Details */}
                {version.statisticalSignificance && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-900 flex items-center space-x-2">
                        <Target className="h-4 w-4 text-blue-600" />
                        <span>Statistical Analysis</span>
                      </h5>
                      <button
                        onClick={() => setShowStatisticalDetails(!showStatisticalDetails)}
                        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {showStatisticalDetails ? 'Hide Details' : 'Show Details'}
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-white p-3 rounded-lg">
                        <span className="text-gray-600">P-value: </span>
                        <span className="font-medium">{safeToFixed(version.statisticalSignificance.pValue, 4)}</span>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <span className="text-gray-600">T-statistic: </span>
                        <span className="font-medium">{safeToFixed(version.statisticalSignificance.tStatistic, 2)}</span>
                      </div>
                      <div className="bg-white p-3 rounded-lg">
                        <span className="text-gray-600">Improvement: </span>
                        <span className={`font-medium ${
                          version.statisticalSignificance.improvement > 0 ? 'text-celadon-600' : 'text-gray-600'
                        }`}>
                          {version.statisticalSignificance.improvement > 0 ? '+' : ''}{safeToFixed(version.statisticalSignificance.improvement, 1)}%
                        </span>
                      </div>
                    </div>

                    {showStatisticalDetails && (
                      <div className="mt-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                        <div className="text-xs text-gray-800 space-y-1">
                          <p><strong>Methodology:</strong> Two-sample t-test with 95% confidence interval</p>
                          <p><strong>Significance threshold:</strong> p &lt; 0.05</p>
                          <p><strong>Minimum sample size:</strong> 30 requests per version for reliable results</p>
                          <p><strong>Effect size:</strong> {safeToFixed(Math.abs(version.statisticalSignificance.improvement), 1)} percentage point change</p>
                          <p><strong>Recommendation:</strong> 
                            <span className={`ml-1 font-medium capitalize ${
                              version.statisticalSignificance.recommendedAction === 'promote' ? 'text-celadon-600' :
                              version.statisticalSignificance.recommendedAction === 'stop' ? 'text-red-600' :
                              'text-gray-500'
                            }`}>
                              {version.statisticalSignificance.recommendedAction}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Performance Recommendations */}
                {version.statisticalSignificance?.recommendedAction === 'promote' && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                    <div className="flex items-start">
                      <TrendingUp className="h-6 w-6 text-green-600 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-green-800 mb-2">
                          🎯 Performance Recommendation
                        </div>
                        <div className="text-sm text-green-700 mb-4">
                          This version shows significant improvement (+{safeToFixed(version.statisticalSignificance.improvement, 1)}%). 
                          Consider promoting it to receive more traffic or using it as a baseline for future experiments.
                        </div>
                        <div className="flex space-x-3">
                          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                            Increase Traffic
                          </button>
                          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
                            Use as Template
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {version.statisticalSignificance?.recommendedAction === 'stop' && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl">
                    <div className="flex items-start">
                      <TrendingDown className="h-6 w-6 text-red-600 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-800 mb-2">
                          ⚠️ Performance Warning
                        </div>
                        <div className="text-sm text-red-700 mb-4">
                          This version shows significant decline ({safeToFixed(version.statisticalSignificance.improvement, 1)}%). 
                          Consider reducing its traffic allocation or stopping it entirely.
                        </div>
                        <div className="flex space-x-3">
                          <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm">
                            Reduce Traffic
                          </button>
                          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm">
                            Pause Version
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Drill-down Content */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-6 bg-gray-50">
                  <InputPatternAnalysis version={version} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Enhanced Footer Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-gray-500 max-w-2xl">
            <strong>Statistical Notes:</strong> Significance calculated using two-sample t-test with 95% confidence level. 
            Confidence intervals use normal approximation. Results are most reliable with sample sizes ≥ 30 per version.
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                const csvData = enhancedAnalytics.overview.map(v => ({
                  version: v.version_name || v.version_id,
                  requests: v.request_count,
                  score: v.avg_auto_eval_score,
                  latency: v.avg_latency,
                  failures: v.failure_count,
                  success_rate: (v.successRate * 100).toFixed(2)
                }));
                
                // Create CSV content
                const headers = Object.keys(csvData[0]).join(',');
                const rows = csvData.map(row => Object.values(row).join(','));
                const csvContent = [headers, ...rows].join('\n');
                
                // Download CSV
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `experiment-analytics-${experiment?.name || 'data'}-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={() => {
                const chartCanvas = document.querySelector('canvas');
                if (chartCanvas) {
                  const url = chartCanvas.toDataURL('image/png');
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `chart-${visualizationType}-${new Date().toISOString().split('T')[0]}.png`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              }}
              className="text-sm text-celadon-600 hover:text-celadon-800 flex items-center space-x-1 px-3 py-2 border border-celadon-300 rounded-lg hover:bg-celadon-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export Chart</span>
            </button>
          </div>
        </div>

        {/* Quick Insights Panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 p-4 bg-gradient-to-r from-celadon-50 to-emerald-50 rounded-lg border border-celadon-200">
          <div className="text-center">
            <div className="text-lg font-bold text-celadon-700">
              {enhancedAnalytics?.overview?.filter(v => v.statisticalSignificance?.significant).length || 0}
            </div>
            <div className="text-sm text-celadon-600">Statistically Significant</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-celadon-700">
              {enhancedAnalytics?.overview?.filter(v => v.statisticalSignificance?.recommendedAction === 'promote').length || 0}
            </div>
            <div className="text-sm text-celadon-600">Recommended for Promotion</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-celadon-700">
              {enhancedAnalytics?.overview?.reduce((sum, v) => sum + (parseInt(v.request_count) || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-celadon-600">Total Requests Analyzed</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Input Pattern Analysis Component
const InputPatternAnalysis = ({ version }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-4">
        <BarChart3 className="h-5 w-5 text-blue-600" />
        <h6 className="font-semibold text-gray-900">Input Pattern Analysis</h6>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="font-medium text-gray-900 mb-3">Pattern Insights</h6>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• Analysis of input patterns that perform well/poorly with this version</p>
            <p>• Common input characteristics, lengths, and topics</p>
            <p>• Performance breakdown by input similarity clusters</p>
            <p>• Correlation between input features and output quality</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h6 className="font-medium text-gray-900 mb-3">Implementation Roadmap</h6>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• Input length distribution and performance correlation</p>
            <p>• Topic clustering using embeddings</p>
            <p>• Common failure patterns by input type</p>
            <p>• Success pattern identification for optimization</p>
          </div>
        </div>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="text-sm text-yellow-800">
          <div className="flex items-center space-x-2 mb-2">
            <Lightbulb className="h-4 w-4" />
            <span className="font-medium">Future Enhancement</span>
          </div>
          <p>Input pattern analysis will be implemented with real clustering and analysis including:</p>
          <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
            <li>Machine learning-based input classification</li>
            <li>Semantic similarity analysis using embeddings</li>
            <li>Performance prediction based on input characteristics</li>
            <li>Automated recommendations for prompt improvements</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdvancedExperimentAnalytics; 